import React, { useCallback, useEffect, useState } from 'react'
import { CreateExternalLocationsDiff } from './diff/CreateExternalLocationsDiff'
import { fetchExternalLocations } from '../../../services/external-locations.service'
import { logError } from '@tomra/datadog-browser-logging'
import { ExternalLocationDiffOverview } from './diff/ExternalLocationDiffOverview'
import { ExternalLocationsOverview } from './ExternalLocationsOverview'

const alphabetically = (a: ExternalLocationType, b: ExternalLocationType) => (a.name < b.name ? -1 : 1)

export const ExternalLocationsPage = () => {
  const [status, setStatus] = useState<RequestStatusType>('idle')
  const [externalLocations, setExternalLocations] = useState<ExternalLocationType[]>([])
  const [diffResponse, setDiffResponse] = useState<ExternalLocationDiffResponseType | null>(null)

  const handleLocationsResponse = useCallback((result: ExternalLocationType[]) => {
    setStatus('idle')
    setExternalLocations(result.sort(alphabetically))
  }, [])

  const handleLocationsError = useCallback((error: any) => {
    if (error.name !== 'AbortError') {
      setStatus('failure')
      logError(new Error('Unable to load external locations'), error)
    }
  }, [])

  useEffect(() => {
    setStatus('pending')

    const { run, abort } = fetchExternalLocations()
    run().then(handleLocationsResponse).catch(handleLocationsError)

    return () => abort()
  }, [handleLocationsError, handleLocationsResponse])

  const getLocations = () => {
    setStatus('pending')
    fetchExternalLocations().run().then(handleLocationsResponse).catch(handleLocationsError)
  }

  const isLoading = status === 'pending'
  const hasLocations = externalLocations.length > 0

  return (
    <>
      <div className="flex items-end justify-between my-4">
        <h1 className="text-xl">External locations</h1>

        {!isLoading && hasLocations && <CreateExternalLocationsDiff onDiffResponseReceived={setDiffResponse} />}
      </div>

      {isLoading ? (
        <div className="centerAbsolute">
          <div className="loadingSpinner" />
        </div>
      ) : status === 'failure' ? (
        <div className="rounded-lg border-l-[3px] border-rose-500 bg-white p-4 text-left shadow-md dark:bg-gray-900 centerAbsolute font-bold w-1/2">
          Something went wrong
        </div>
      ) : hasLocations ? (
        <ExternalLocationsOverview allLocations={externalLocations} />
      ) : (
        <div className="rounded-2xl bg-white shadow-md dark:bg-gray-900 p-6 flex flex-col items-center">
          <h2 className="text-lg mb-2">No external locations yet</h2>
          <p className="mb-4">Please select a file to upload.</p>
          <CreateExternalLocationsDiff onDiffResponseReceived={setDiffResponse} />
        </div>
      )}

      {diffResponse && (
        <ExternalLocationDiffOverview
          fileId={diffResponse.fileId}
          diff={diffResponse.diff}
          onDiffApplied={() => {
            getLocations()
            setDiffResponse(null)
          }}
          onDiffRejected={() => setDiffResponse(null)}
        />
      )}
    </>
  )
}
